import React, { useState, useEffect } from 'react';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { Avatar } from 'components/modules/user/Avatar';
import Badge from 'components/v2/Badge';
import { useTranslation } from 'react-i18next';
import EditButton from 'components/v2/buttons/EditButton';
import CargomaticCard from 'components/v2/card';
import LabelValue from 'components/ui/tailwind/label-value';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import AuditHistory from 'components/modules/shipments/AuditHistory';
import AccountContacts from 'components/v2/contacts';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import PhoneField from 'components/v2/forms/PhoneField';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import { useParams } from 'react-router-dom';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import CustomerStore from 'stores/CustomerSearchStore';
import { isEqual } from 'lodash';
import { sluggify } from 'utils/localization/utils';
import WarehouseUserSettings from './WarehouseUserSettings';

const WarehouseUserDetails = ({ details, onOpenPassword }) => {
  const { t } = useTranslation();
  const params = useParams();
  const { id } = params;
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [user, setUser] = useState({ ...details });
  const [page, setPage] = useState(1);
  const [auditItems, setAuditItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formChanged, setFormChanged] = useState(false);

  const userId = id || details._id;

  const handleAuditChange = () => {
    const updatedItems = CustomerStore.getCustomerAudits();
    if (!isEqual(updatedItems, auditItems)) {
      setAuditItems(updatedItems);
      setIsLoading(false);
    }
  };

  const fetchAuditEvents = () => {
    CustomersActionCreators.fetchCustomerAudits(id, { page });
  };

  useEffect(() => {
    CustomerStore.addAuditChangeListener(handleAuditChange);
    fetchAuditEvents();
  }, [page]);

  const renderSidebar = () => {
    return <WarehouseUserSettings userId={details._id} activePage="profile" />;
  };

  const breadcrumbs = {
    pages: [
      { name: t('WAREHOUSE_USER'), href: `/users/${userId}`, current: true },
    ],
  };

  const renderEditAccountForm = () => {
    const REQUIRED_FIELDS = ['first_name', 'last_name', 'email', 'phone'];
    const getRequiredAsterik = (field) =>
      REQUIRED_FIELDS.includes(field) ? '*' : '';

    const handleFieldChange = (field, value) => {
      if (!formChanged) {
        setFormChanged(true);
      }
      setUser((prev) => ({ ...prev, [field]: value }));
    };

    const validateFields = () => {
      const error = {};
      REQUIRED_FIELDS.forEach((field) => {
        if (!user[field]) {
          error[field] = {
            message: t('FIELD_REQUIRED_TIP', {
              field: titleize(t(sluggify(field))),
            }),
          };
        }
      });
      if (Object.keys(error).length) {
        setErrors(error);
        return false;
      }
      setErrors({});
      return true;
    };

    const handleSave = async () => {
      if (validateFields()) {
        const updates = { _id: details._id };
        Object.keys(user).forEach((key) => {
          if (user[key] !== details[key]) {
            if (key === 'first_name' || key === 'last_name') {
              const name = `${user.first_name} ${user.last_name}`.trim();
              updates.name = name;
              updates.optimized_name = name.toUpperCase();
            }
            updates[key] = (user[key] || '').trim();
          }
        });
        if (Object.keys(updates).length > 1) {
          const result = await CustomersAPIUtils.updateWarehouseUser(updates);
          if (result?.success && result?.user) {
            setShowEditAccountModal(false);
            setUser(result.user);
            setFormChanged(false);
          }
        }
      }
    };

    const handleCancel = () => {
      if (formChanged) {
        setUser(details);
      }
      setShowEditAccountModal(false);
    };

    const { first_name, last_name, email, phone } = user;

    const formKey = 'warehouse-edit-form';

    return (
      <div data-testid={`${formKey}-page`}>
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <CargomaticInput
            id={`${formKey}-first-name`}
            error={errors?.first_name}
            className="sm:col-span-3"
            value={unescapeSpecialChars(first_name)}
            label={`${titleize(t('FIRST_NAME'))} ${getRequiredAsterik(
              'first_name',
            )}`}
            info={t('WAREHOUSE_USER_FIRST_NAME')}
            onChange={(val) => handleFieldChange('first_name', val)}
          />

          <CargomaticInput
            id={`${formKey}-last-name`}
            error={errors?.last_name}
            className="sm:col-span-3"
            value={unescapeSpecialChars(last_name)}
            label={`${titleize(t('LAST_NAME'))} ${getRequiredAsterik(
              'first_name',
            )}`}
            info={t('WAREHOUSE_USER_LAST_NAME')}
            onChange={(val) => handleFieldChange('last_name', val)}
          />

          <div data-testid={`${formKey}-phone`} className="sm:col-span-3">
            <label className="form-label mb-1">{`${titleize(
              t('PHONE'),
            )} ${getRequiredAsterik('phone')}`}</label>
            <PhoneField
              id="phone"
              onChange={(val) => handleFieldChange('phone', val)}
              value={phone}
            />
          </div>

          <CargomaticInput
            id={`${formKey}-email`}
            error={errors?.email}
            className="sm:col-span-4"
            label={`${titleize(t('EMAIL'))} ${getRequiredAsterik('email')}`}
            info={t('WAREHOUSE_USER_EMAIL')}
            onChange={(val) => handleFieldChange('email', val)}
            value={email}
            maxLength={254}
          />
        </div>
        <div
          data-testid={`${formKey}-footer`}
          className="flex justify-end my-2"
        >
          <button
            data-testid={`${formKey}-cancel-button`}
            className="justify-center button button-secondary"
            onClick={handleCancel}
          >
            {t('CANCEL')}
          </button>
          <button
            data-testid={`${formKey}-save-button`}
            className="mx-2 button button-green"
            onClick={handleSave}
          >
            {titleize(t('SAVE'))}
          </button>
        </div>
      </div>
    );
  };

  const { first_name, last_name, phone, email } = details;
  const pageKey = 'warehouse-profile';
  return (
    <LayoutWithSidebar sidebar={renderSidebar} breadcrumbs={breadcrumbs}>
      <div
        data-testid={`${pageKey}-page`}
        className="flex flex-wrap lg:-mx-6 lg:mt-0 mt-4"
      >
        <div className="w-full lg:w-1/3 lg:px-6">
          <CargomaticCard>
            <div className="flex lg:flex-col space-x-2">
              <div
                data-testid={`${pageKey}-avatar`}
                className="flex  lg:justify-center mb-2"
              >
                <Avatar
                  key={userId}
                  userId={userId}
                  className="lg:h-32 lg:w-32 w-20 h-20 cursor-pointer rounded-full"
                />
              </div>
              <div className="flex flex-col justify-center lg:items-center -mx-1 lg:text-gray-dark">
                <div className="pl-2 ">
                  <h4 className="">
                    {first_name} {last_name}
                  </h4>
                </div>
                <div data-testid={`${pageKey}-badge`} className="px-1 mb-2">
                  <Badge color="blue" rounded size="large">
                    {titleize(t('WAREHOUSE_USER'))}
                  </Badge>
                </div>
              </div>
            </div>
          </CargomaticCard>

          <div data-testid={`${pageKey}-account-section`} className="mt-6">
            <CargomaticCard
              header={() => (
                <header
                  className="cursor-pointer group"
                  onClick={() => setShowEditAccountModal(true)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="my-0">{titleize(t('ACCOUNT'))}</h3>
                    <EditButton />
                  </div>
                </header>
              )}
            >
              <LabelValue
                value={`${unescapeSpecialChars(
                  first_name,
                )} ${unescapeSpecialChars(last_name)}`}
                icon="user"
              />
              <LabelValue
                value={createPhoneNumberWithExtension(phone, '', t('EXT'))}
                icon="phone"
              />
              <LabelValue value={`${email}`} icon="envelope" />
            </CargomaticCard>
          </div>
          <div className="my-6">
            <button
              data-testid={`${pageKey}-change-password-button`}
              onClick={() => onOpenPassword()}
              className="w-full button button-hollow-red"
            >
              {t('CHANGE_PASSWORD')}
            </button>
          </div>
        </div>
        <div
          data-testid={`${pageKey}-history-section`}
          className="w-full lg:w-2/3 lg:px-6"
        >
          <CargomaticCard
            header={() => (
              <h3 className="my-0 text-xl font-medium">{t('AUDIT_HISTORY')}</h3>
            )}
            removeHorizontalPadding
            removeVerticalPadding
          >
            <AuditHistory
              audits={auditItems}
              keepDuplicates
              paginate
              onClick={(page) => setPage(page)}
              currentPage={page}
              isLoading={isLoading}
            />
          </CargomaticCard>
          <div data-testid={`${pageKey}-contact-section`} className="mt-6">
            <AccountContacts contacts={details.contacts} userId={userId} />
          </div>
        </div>
      </div>
      {showEditAccountModal && (
        <CargomaticModal
          show
          onHide={() => setShowEditAccountModal(false)}
          title={t('EDIT_ACCOUNT')}
          backdrop="static"
          footer={null}
          location="middle"
          body={renderEditAccountForm()}
        />
      )}
    </LayoutWithSidebar>
  );
};

export default WarehouseUserDetails;
